import * as groupService from "../../../services/groups";
import * as settings from "../../../services/settings";

import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Col, Form, Input, Row, Spin, Table, Typography, notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { ModalDelete } from "../../../Components/model/modal/delete";
import { SearchOutlined } from "@ant-design/icons";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const initPage = { limit: 10, page: 1 };
const { Link } = Typography;
export const RoleManagementPage = () => {
  const { accessToken } = useAccessToken();
  const hasFetched = useRef(false);
  const { hasPermission, isSuperuser } = usePermission();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initPage);
  const [groupsData, setGroupsData] = useState([]);
  const [form] = Form.useForm();
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [updateWarehouses, setUpdateWarehouses] = useState();
  const { t } = useTranslation("group", "buttons");
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});

  const { Title } = Typography;

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };
  const fetchGroupPermissionManagement = useCallback(
    async (param) => {
      setLoading(true);
      try {
        const response = await groupService.getGroups(accessToken, param);
        setSearch({ ...param });
        setTotal(response.totalCount);
        setGroupsData(response.query);
      } catch (err) {
        console.error("[RoleManagementPage] fetchGroupPermissionManagement: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  const onFinish = (value) => {
    const values = {
      name: value["search"],
    };
    search.page = 1;
    fetchGroupPermissionManagement({ ...search, values });
  };

  const items = [
    {
      title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span>,
    },
    {
      title: (
        <a href="/usermanagement/groups" style={{ fontWeight: 600, color: "black" }}>
          {t("Title_Group")}
        </a>
      ),
    },
  ];

  const columns = [
    {
      title: () => {
        return <>{t("Table_Group.Table_ID")}</>;
      },
      dataIndex: "id",
      key: "id",
      render: (_, row) => {
        return (
          <Link
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/usermanagement/group/create", {
                state: { type: "EDIT", value: row },
              });
            }}
          >
            {row.id}
          </Link>
        );
      },
    },
    {
      title: () => {
        return <>{t("Table_Group.Table_name")}</>;
      },
      dataIndex: "name",
      key: "name",
    },
    {
      title: () => {
        return <>{t("Table_Group.Table_Edit")}</>;
      },
      dataIndex: "edit",
      key: "edit",
      render: (_, record) => {
        return (
          <p>
            <Button
              type="link"
              disabled={!hasPermission(permissions.Group.CHANGE)}
              onClick={() => {
                navigate("/usermanagement/group/create", {
                  state: { type: "EDIT", value: record },
                });
              }}
            >
              <EditOutlined />
            </Button>
          </p>
        );
      },
    },
    {
      title: () => {
        return <>{t("Table_Group.Table_Delete")}</>;
      },
      dataIndex: "delete",
      key: "delete",
      render: (_, record) => {
        return (
          <p>
            <Button
              type="link"
              disabled={!hasPermission(permissions.Group.DELETE)}
              onClick={() => {
                setIsOpenDelete(true);
                setPrepareDeleteData(record);
              }}
            >
              <DeleteOutlined />
            </Button>
          </p>
        );
      },
    },
  ];

  const onDeleteUser = async (data) => {
    let prepareDeleteData = {
      id: data.id,
    };
    setLoading(true);
    try {
      const response = await groupService.deleteGroup(accessToken, prepareDeleteData);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{response}</>);
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchGroupPermissionManagement({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[RoleManagementPage] onDeleteUser: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken || hasFetched.current) return;
    hasFetched.current = true;
    fetchGroupPermissionManagement(search);
  }, [accessToken]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={items}></Breadcrumb>
            <Row style={{ padding: "15px 0px" }}>
              <Col span={12}>
                <Title
                  level={4}
                  style={{
                    textWrap: "nowrap",
                    display: "flex",
                    gap: "7px",
                  }}
                >
                  <ArrowLeftOutlined style={{ fontSize: "20px" }} />
                  {t("Title_Group")}
                </Title>
              </Col>
            </Row>
            <Form
              layout="inline"
              form={form}
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  form.submit();
                }
              }}
              style={{ flexWrap: "nowrap", overflowX: "auto", minWidth: 280 }}
            >
              <Col span={20} style={{ minWidth: 200 }}>
                <Form.Item name="search">
                  <Input size="default" placeholder="Search" prefix={<SearchOutlined />} style={{ minWidth: 200 }} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item>
                  <Button style={{ width: "100%" }} type="primary" icon={<SearchOutlined />} htmlType="submit">
                    {t("BTN_Search", { ns: "buttons" })}
                  </Button>
                </Form.Item>
              </Col>
            </Form>
          </Col>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "1rem",
              marginLeft: "-1rem",
            }}
          >
            <Button
              // className="flex justify-end"
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => {
                navigate("/usermanagement/group/create", {
                  state: { type: "CREATE" },
                });
              }}
            >
              {t("BTN_Create", { ns: "buttons" })}
            </Button>
            {/* <Button
              style={{ marginLeft: '1rem' }}
              // className="flex justify-end"
              type='primary'
              icon={<PlusCircleOutlined />}
              onClick={() => {
                navigate('/usermanagement/group/create', {
                  state: { type: 'EXPERORT' },
                })
              }}
            >
              {t('BTN_Export', { ns: 'buttons' })}
            </Button> */}
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Table
                loading={loading}
                scroll={{ x: "max-content" }}
                columns={columns}
                dataSource={groupsData}
                rowKey={(record) => record.id}
                pagination={{
                  defaultCurrent: search.limit,
                  current: search.page,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  total: total,
                  onChange: (page, limit) => {
                    fetchGroupPermissionManagement({ ...search, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            onDeleteUser(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
